import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import { PASSWORD_RULES, failedPasswordRules, passwordStrengthScore } from '@erp/shared';
import { cn } from '@/lib/utils';

const SCORE_CLASSES = [
  'bg-destructive',
  'bg-destructive',
  'bg-amber-500',
  'bg-amber-500',
  'bg-emerald-500',
] as const;

export interface PasswordStrengthProps {
  password: string;
}

/** Zeigt Richtlinienerfüllung und eine grobe Stärkeeinschätzung des Passworts. */
export function PasswordStrength({ password }: PasswordStrengthProps): JSX.Element {
  const { t } = useTranslation();
  const failed = new Set(failedPasswordRules(password));
  const score = passwordStrengthScore(password);
  const barClass = SCORE_CLASSES[score] ?? SCORE_CLASSES[0];

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div
          className="flex h-1.5 flex-1 gap-1"
          role="meter"
          aria-valuemin={0}
          aria-valuemax={4}
          aria-valuenow={score}
          aria-label={t('security.password.strengthLabel')}
        >
          {[0, 1, 2, 3].map((segment) => (
            <span
              key={segment}
              className={cn('h-full flex-1 rounded-full', segment < score ? barClass : 'bg-muted')}
            />
          ))}
        </div>
        <span className="w-24 text-right text-xs text-muted-foreground">
          {t(`security.password.strength.${String(score)}`)}
        </span>
      </div>
      <ul className="grid gap-1 sm:grid-cols-2">
        {PASSWORD_RULES.map((rule) => {
          const satisfied = password.length > 0 && !failed.has(rule);
          return (
            <li
              key={rule}
              className={cn(
                'flex items-center gap-1.5 text-xs',
                satisfied ? 'text-muted-foreground' : 'text-foreground',
              )}
            >
              {satisfied ? (
                <Check className="h-3.5 w-3.5 text-emerald-500" aria-hidden="true" />
              ) : (
                <X className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
              )}
              {t(`security.password.rules.${rule}`)}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
